<template>
  <div class="tab-container"
       role="tablist"
       :aria-label="label"
       @keydown="onKeyDown">
    <animated-dropdown
      :options="dropdownOptions"
      :select-multiple="false"
      :show-label-when-selected="false"
      close-on-select
      class="dropdown"
      :index="1"
      label="Select tab"
      :selected="[value]"
      :class="dropdownClasses"
      @changed="dropdownSelected">
    </animated-dropdown>
    <button
      v-for="(tab, index) in tabs"
      :id="id + '-tab-' + index"
      :key="tab.key"
      role="tab"
      :aria-selected="isSelected(tab.key)"
      :aria-controls="id + '-panel-' + index"
      :tabindex="isSelected(tab.key) ? 0 : -1"
      class="tab"
      :class="{ 'tab--selected': isSelected(tab.key), 'tab--mobile': mobileDropdown }"
      :style="tabStyle"
      @click="tabSelected(tab.key)">
      {{ tab.label }}
    </button>
  </div>
</template>

<script>
import AnimatedDropdown from "~/Components/Base/AnimatedDropdown.vue";
import { KeyEventCodes } from "~/Scripts/mixins/common";

export default {
  name: "DropdownTabSelector",
  components: {
    AnimatedDropdown,
  },
  mixins: [KeyEventCodes],
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: null,
    },
    tabs: {
      type: Array,
      default: null,
    },
    layout: {
      type: String,
      default: "horizontal",
    },
    mobileDropdown: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      selectedTab: null,
      focusedTabIndex: 0,
    };
  },
  computed: {
    dropdownOptions () {
      return this.tabs.map( t => ( {
        key: t.key,
        value: t.label,
      } ) );
    },
    tabStyle () {
      let result = null;

      if ( this.layout === "horizontal" ) {
        const width = 100 / this.tabs.length;
        result = { width: `${width}%` };
      }

      return result;
    },
    dropdownClasses () {
      return {
        "dropdown--mobile": this.mobileDropdown,
      };
    },
  },
  watch: {
    value: {
      immediate: true,
      handler ( val ) {
        console.log( "value changed!", val );
        this.selectedTab = val;
      },
    },
    selectedTab ( val ) {
      if ( val ) {
        this.$emit( "input", val );
      }
    },
  },
  methods: {
    isSelected ( tab ) {
      return tab === this.selectedTab;
    },
    tabSelected ( tab ) {
      this.selectedTab = tab;
      this.$emit( "tab-selected", tab );
    },
    dropdownSelected ( val ) {
      if ( val && val.length > 0 ) {
        this.tabSelected( val[0] );
      }
    },
    onKeyDown ( event ) {
      const { target: element, keyCode } = event;
      const {
        LEFT, RIGHT, UP, DOWN,
      } = this.keyboardEvent.keyCode;

      element.setAttribute( "tabindex", -1 );

      if ( keyCode === RIGHT || keyCode === DOWN || keyCode === LEFT || keyCode === UP ) {
        event.preventDefault();
      }

      if ( keyCode === RIGHT || keyCode === DOWN ) {
        this.focusedTabIndex += 1;
        if ( this.focusedTabIndex >= this.tabs.length ) {
          this.focusedTabIndex = 0;
        }
      } else if ( keyCode === LEFT || keyCode === UP ) {
        this.focusedTabIndex -= 1;
        if ( this.focusedTabIndex < 0 ) {
          this.focusedTabIndex = this.tabs.length - 1;
        }
      }
      const nextElement = document.getElementById( `${this.id}-tab-${this.focusedTabIndex}` );
      if ( nextElement ) {
        nextElement.setAttribute( "tabindex", 0 );
        nextElement.focus();
      }
    },
  },
};
</script>

<style lang="less" scoped>
.tab-container {
  .dropdown {
    display: none;
    width: 100%;
    margin-right: 0;

    /deep/ .checkbox {
      background-color: #fff;
    }

    &--mobile {
      @media (max-width: 767px) {
        display: block;
      }
    }
  }

  .tab {
    display: block;
    text-align: left;
    border: none;
    background: none;
    color: inherit;
    font-weight: inherit;

    &--mobile {
      @media (max-width: 767px) {
        display: none !important;
      }
    }
  }
}
</style>
